import { useCallback, useMemo } from 'react'

import { useInvalidateLatestControllerConfiguration } from '@/features/controller/configuration/hooks/useLatestControllerConfiguration'
import { useUpdateControllerConfigLatestVersionMutation } from '@/features/controller/configuration/hooks/useUpdateControllerConfigLatestVersionMutation'
import type { Resource, ResourceConfig } from '@/features/controller/configuration/models/resourceConfigSchema'
import { ControllerConfigTypeId } from '@/features/controller/configuration/types'

export type LocalControlConfigUpdate = Readonly<{
  /** Echo of the loaded version's id — server uses this as an optimistic-locking token. */
  previousVersionId: string
  /** The full `resource.json` document currently loaded; the patch is merged into this. */
  currentConfig: ResourceConfig
  /**
   * The full updated `Resource` (matched by `id`). The entire entry is replaced.
   * Other resources in the document are untouched.
   */
  updatedResource: Resource
  /** Optional human-readable comment persisted on the new config version. */
  comment?: string
}>

export type UseUpdateLocalControlConfigMutationResult = Readonly<{
  updateLocalControlConfig: (params: LocalControlConfigUpdate) => Promise<void>
  isPending: boolean
  isError: boolean
  error: Error | null
}>

function mergeResourceIntoConfig(currentConfig: ResourceConfig, updatedResource: Resource): ResourceConfig {
  const currentResources = currentConfig.resources ?? []
  const matchIndex = currentResources.findIndex((r) => r.id === updatedResource.id)
  if (matchIndex === -1) {
    throw new Error(`Resource with id "${updatedResource.id}" not found in resource.json`)
  }
  const nextResources = [...currentResources]
  nextResources[matchIndex] = updatedResource
  return { ...currentConfig, resources: nextResources }
}

/**
 * Wraps `useUpdateControllerConfigLatestVersionMutation` with a `resource.json`-aware merge step:
 * accepts a single updated `Resource`, replaces the matching entry in the full document by `id`,
 * and PUTs the merged document back as the next config version.
 *
 * On success, invalidates the underlying `useLatestControllerConfiguration` query so the new
 * server-authoritative value is re-displayed.
 */
export function useUpdateLocalControlConfigMutation(controllerId: string): UseUpdateLocalControlConfigMutationResult {
  const { updateControllerConfigLatestVersion, isPending, isError, error } =
    useUpdateControllerConfigLatestVersionMutation(controllerId, ControllerConfigTypeId.ResourceJson)
  const invalidate = useInvalidateLatestControllerConfiguration()

  const updateLocalControlConfig = useCallback(
    async ({ previousVersionId, currentConfig, updatedResource, comment }: LocalControlConfigUpdate): Promise<void> => {
      const nextConfig = mergeResourceIntoConfig(currentConfig, updatedResource)
      await updateControllerConfigLatestVersion({
        previousVersionId,
        value: JSON.stringify(nextConfig),
        comment,
      })
      await invalidate({ controllerId, configTypeId: ControllerConfigTypeId.ResourceJson })
    },
    [controllerId, invalidate, updateControllerConfigLatestVersion],
  )

  return useMemo(
    () => ({ updateLocalControlConfig, isPending, isError, error }),
    [updateLocalControlConfig, isPending, isError, error],
  )
}
